import { z } from 'zod';
import { defineStepType } from '../contract.js';
/**
 * A compliance obligation this system TRACKS but deliberately does not COLLECT.
 *
 * Form I-9 is the case that produced it. QuickBooks Workforce is the external system of
 * record, so the dashboard must never collect or display the form, its document numbers, or
 * anyone's citizenship or immigration status — and must never claim to have verified
 * employment authorisation. What HR still needs is to know where the obligation stands, and
 * what the candidate needs is to be told plainly that it happens somewhere else.
 *
 * The alternative — leaving the requirement with no step at all — is what the catalog did
 * before, and it produced a candidate who finished everything the portal asked and stayed
 * "Not ready" with nothing on screen explaining why.
 *
 * `submittedBy: 'office'` is the security boundary, not a label. The portal renders a
 * read-only status for this type, and the server refuses a person-submitted office step
 * outright (`apps/api/src/functions/uploads.ts`), so a candidate cannot assert their own
 * completion however the client behaves. That refusal is the mechanism behind "candidate
 * self-assertion alone must never produce completed_external".
 *
 * WHAT IS DELIBERATELY ABSENT from both schemas, and must stay absent: document types,
 * document numbers, expiry dates of identity documents, citizenship or immigration status,
 * images, and any copy of the form. The note field is bounded and general on purpose. If a
 * future change wants to store one of those, that is a privacy decision, not a schema edit.
 */
/**
 * Where the obligation stands. Not the step's own lifecycle — the engine already has one of
 * those (`not_started → sent → submitted → approved`). This is the state of the process
 * happening in the other system, which is why it lives on the submission.
 */
export const externalProcessStateSchema = z.enum([
    /** Nobody has started it. The invitation has not been recorded as sent. */
    'not_started',
    /** In flight in the external system. WHICH half is in flight is the phase, below. */
    'pending_external',
    /** HR has confirmed, against the external system, that it is done. */
    'completed_external',
    /** Something is wrong that a person must look at — a bounce, a mismatch, a stall. */
    'needs_hr_attention',
]);
/**
 * Which half of the external workflow is outstanding.
 *
 * Form I-9 has two halves in different hands: the employee completes Section 1, then the
 * employer examines acceptable documents and completes Section 2. "Pending" without saying
 * which is a materially different operational fact — one is chase the candidate, the other is
 * do our own work — and HR cannot act on the difference if it is not recorded.
 *
 * A separate field rather than two more states, so the four-state vocabulary that already
 * ships stays valid and nothing reading `state` has to change. And a FIELD rather than the
 * note: workflow state carried in free text cannot be queried, cannot be validated, and
 * drifts the moment two people phrase it differently.
 */
export const externalProcessPhaseSchema = z.enum([
    /** Waiting on the person: their Section 1 is not done. */
    'employee',
    /** Waiting on us: their part is done, our Section 2 review is not. */
    'hr_verification',
]);
/**
 * Where the obligation stands, mapped onto the STEP's own lifecycle — and the reason this
 * mapping exists rather than "approve it and read the submission".
 *
 * The I-9 requirement is satisfied by `all-steps` over `step-instances`: the readiness engine
 * asks whether every step satisfying it is `approved`, and never looks inside a submission.
 * So an instance approved while its checkpoint said `pending_external` marked the I-9
 * SATISFIED — a person could reach "ready for a first assignment" on an I-9 that HR had just
 * recorded as outstanding, or as needing attention. The step type's `onApprove` correctly
 * emitted no verification, but nothing was reading the verification.
 *
 * Only a confirmed completion approves the instance. The other three states leave it short of
 * approved, which is what makes "the requirement stays unsatisfied while the obligation is
 * merely in flight" a property of the system rather than a sentence in a comment.
 */
export const externalProcessInstanceState = {
    /** Issued and sitting in the packet, so the candidate can see it. Nothing has happened. */
    not_started: 'sent',
    /** Something is happening elsewhere. Visible progress, not proof. */
    pending_external: 'in_progress',
    /** Also not proof — and never `rejected`, which in this vocabulary means the CANDIDATE must redo something. */
    needs_hr_attention: 'in_progress',
    /** The only state that is evidence, and the only one that approves. */
    completed_external: 'approved',
};
/**
 * How a completion was established. A closed vocabulary with one member today, and that is
 * the point: when a QuickBooks integration lands it adds a value here and nothing else in
 * the domain model moves. A completion with no stated source is a completion nobody can
 * audit.
 */
export const externalConfirmationSourceSchema = z.enum(['quickbooks_manual_confirmation']);
export const externalProcess = defineStepType({
    type: 'external-process',
    label: 'Completed in another system',
    /**
     * HR's work, never the candidate's. See `submittedBy` on the contract for why this is a
     * boundary the server enforces rather than a description.
     */
    submittedBy: 'office',
    description: 'An obligation completed outside this system — the dashboard records only where it stands, never the form or its contents.',
    configSchema: z.object({
        heading: z.string().min(1),
        /** Which external system owns it. Shown to HR; drives no behaviour yet. */
        externalSystem: z.enum(['quickbooks-workforce']),
        /**
         * What the CANDIDATE is told. Held in configuration rather than in a component so the
         * wording is reviewable and versioned with the definition, like every other person-facing
         * string in this catalog.
         */
        candidateGuidance: z.string().min(1),
        /** What HR is told, on the person page. */
        instructions: z.string().default(''),
        /**
         * A candidate-specific link, ONLY if a security-reviewed integration supplies one.
         * Null until then, and the portal says HR will send the invitation separately rather
         * than inventing a URL. A hard-coded sign-up link would send candidates somewhere this
         * system cannot vouch for.
         */
        externalUrl: z.string().url().nullable().default(null),
        requirementKey: z.string().min(1).nullable().default(null),
    }),
    submissionSchema: z
        .object({
        state: externalProcessStateSchema,
        /**
         * Only meaningful while `pending_external`, and required there. Null in every other
         * state, because "not started, waiting on the employee" is a contradiction and a stored
         * one would eventually be read as true.
         */
        phase: externalProcessPhaseSchema.nullable().default(null),
        /**
         * When HR recorded that the Workforce invitation was sent. NOT proof of anything about
         * the form — an invitation is not a Section 1, and this exists so HR can tell a stall
         * from a fresh request when deciding whether to chase.
         */
        invitationRecordedAt: z.string().datetime().nullable().default(null),
        /**
         * The Entra object id of the HR user who set this, stamped by the API from the signed-in
         * principal — never typed, and never a candidate.
         */
        confirmedBy: z.string().min(1),
        confirmedAt: z.string().datetime(),
        /** Null except on a completion, where it is required. See the refinement below. */
        source: externalConfirmationSourceSchema.nullable().default(null),
        /**
         * Bounded, general, and person-facing-safe. Deliberately NOT a place for document
         * details: 200 characters is enough for "invited 3 Sep, chased 10 Sep" and too few for
         * anything that belongs in the external system.
         */
        note: z.string().max(200).default(''),
    })
        /**
         * The completion rule, in one place.
         *
         * `completed_external` means an HR_Admin has confirmed BOTH sections are complete and the
         * final form is retrievable in the external system. It is not implied by an invitation
         * being sent, by the candidate finishing their half, by a document existing, or by the
         * person appearing in the external system's roster — the isolated test invitation proved
         * exactly that: it created an employee record and completed no I-9 at all.
         */
        .refine((value) => value.state !== 'completed_external' || value.source !== null, {
        message: 'A completed external process must record how it was confirmed.',
        path: ['source'],
    })
        .refine((value) => value.state !== 'pending_external' || value.phase !== null, {
        message: 'A pending external process must say which half is outstanding.',
        path: ['phase'],
    })
        .refine((value) => value.state === 'pending_external' || value.phase === null, {
        message: 'Only a pending external process has a phase.',
        path: ['phase'],
    }),
    /**
     * Only a confirmed completion produces evidence. The other three states are visible
     * progress, not proof — recording a verification for `pending_external` would make the
     * requirement read as satisfied because somebody sent an invitation.
     */
    onApprove: ({ instance, config, submission }) => [
        ...(submission.state === 'completed_external' && config.requirementKey !== null
            ? [
                {
                    kind: 'record-verification',
                    personId: instance.personId,
                    requirementKey: config.requirementKey,
                    method: 'external-system',
                    verifiedBy: submission.confirmedBy,
                },
            ]
            : []),
        { kind: 'recompute-rollup', personId: instance.personId },
    ],
    /** Nothing here expires: the external system owns the document and its dates. */
    expiresAt: () => null,
});
//# sourceMappingURL=external-process.js.map